/**
 * Mission Dresseur — Icon Registry (single source of truth).
 *
 * Seven classified families. Consumers import from "@/brand/icons" only —
 * never directly from lucide-react. This lets us swap, alias, or augment
 * icons globally without touching pages.
 *
 * Families
 *   - BrandIcon       : custom Mission Dresseur symbols (mission, classeur, expédition…)
 *   - ActionIcon      : interaction triggers (Plus, ArrowRight, Pencil…)
 *   - StatusIcon      : feedback / state (Check, AlertTriangle, Info…)
 *   - RarityIcon      : rarity glyphs (Star variants, custom holo)
 *   - NavigationIcon  : nav/sidebar (Compass, BookOpenCheck, User…)
 *   - DocumentIcon    : PDF, articles, files (FileText, Download, Printer…)
 *   - SocialIcon      : external networks (Twitter, Discord…)
 */
import {
    // Actions
    Plus, ArrowRight, ArrowLeft, X, Pencil, Trash2, Search, Filter, MoreHorizontal,
    Save, Copy, ExternalLink, Download, Upload, RefreshCw, ChevronDown, ChevronUp,
    ChevronRight, ChevronLeft, Eye, EyeOff,
    // Status / feedback
    Check, CheckCircle, AlertTriangle, AlertCircle, Info, XCircle, Loader2,
    Lock, Unlock,
    // Navigation
    Compass, BookOpenCheck, BookOpen, LayoutDashboard, Users, Settings, FileBarChart,
    LogOut, User, Home, Library, Sparkles,
    // Document
    FileText, FileImage, FileCode, FileDown, Printer, FilePlus,
    // Social
    Twitter, Github, Mail, MessageCircle,
    // Theme + misc
    Sun, Moon, Star, Rocket, Map, Heart,
} from "lucide-react";

// ----- Custom Mission Dresseur brand icons -----
// These are SVGs designed in-system, kept stroke-1.6 to match Lucide rhythm.
import React from "react";

const IconBase = ({ children, size = 24, strokeWidth = 1.6, className = "", ...rest }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
        {...rest}
    >{children}</svg>
);

export const MissionIcon = (p) => (
    <IconBase {...p}><path d="M3 11.2 12 4l9 7.2v8.3a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 19.5z"/><path d="M9 21V13h6v8"/><circle cx="12" cy="16.5" r="1.2" fill="currentColor"/></IconBase>
);
export const ClasseurIcon = (p) => (
    <IconBase {...p}><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 3v18M16 7h-4M16 12h-4M16 17h-4"/></IconBase>
);
export const ExpeditionIcon = (p) => (
    <IconBase {...p}><polygon points="3 6 9 4 15 6 21 4 21 18 15 20 9 18 3 20"/><path d="M9 4v14M15 6v14"/></IconBase>
);
export const DresseurIcon = (p) => (
    <IconBase {...p}><circle cx="12" cy="9" r="3.5"/><path d="M5.5 20a6.5 6.5 0 0 1 13 0"/><path d="M9 7.5 12 4l3 3.5"/></IconBase>
);
export const DoublonIcon = (p) => (
    <IconBase {...p}><rect x="3" y="6" width="12" height="14" rx="1.5"/><rect x="9" y="3" width="12" height="14" rx="1.5"/></IconBase>
);
export const RevealCardIcon = (p) => (
    <IconBase {...p}><rect x="4" y="3" width="13" height="18" rx="2"/><path d="m17 3 4 4-4 4"/><path d="M21 7H10"/></IconBase>
);
export const HoloGlyphIcon = (p) => (
    <IconBase {...p}><path d="m12 3 2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 15.4l-4.8 2.5.9-5.4-3.9-3.8 5.4-.8z"/><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none"/></IconBase>
);
export const StackFlipIcon = (p) => (
    <IconBase {...p}><rect x="3" y="5" width="14" height="14" rx="2"/><path d="M17 9h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-2"/></IconBase>
);

// ----- Classified families -----
export const BrandIcon = {
    Mission: MissionIcon,
    Classeur: ClasseurIcon,
    Expedition: ExpeditionIcon,
    Dresseur: DresseurIcon,
    Doublon: DoublonIcon,
    RevealCard: RevealCardIcon,
    HoloGlyph: HoloGlyphIcon,
    StackFlip: StackFlipIcon,
};

export const ActionIcon = {
    Plus, ArrowRight, ArrowLeft, Close: X, Pencil, Trash: Trash2, Search, Filter,
    More: MoreHorizontal, Save, Copy, ExternalLink, Download, Upload, Refresh: RefreshCw,
    ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Eye, EyeOff,
};

export const StatusIcon = {
    Check, CheckCircle, Warning: AlertTriangle, Alert: AlertCircle, Info,
    Error: XCircle, Loader: Loader2, Lock, Unlock,
};

export const NavigationIcon = {
    Compass, Mission: BookOpenCheck, Library, Home, Dashboard: LayoutDashboard,
    Users, Settings, Reports: FileBarChart, LogOut, User, Sparkles, Book: BookOpen, Map,
};

export const DocumentIcon = {
    FileText, FileImage, FileCode, FileDown, FilePlus, Printer,
};

export const SocialIcon = {
    Twitter, Github, Mail, Discord: MessageCircle,
};

export const ThemeIcon = { Sun, Moon };
export const MiscIcon = { Star, Rocket, Heart };

// Convenience re-exports — for migration paths that still expect Lucide-style names
export { Star, Rocket, Heart, Plus, ArrowRight };
